/*
 * Sticky add to cart bar for the main product section.
 *
 * The bar never owns the cart logic: it mirrors the state of the real product
 * form and forwards its clicks to the real submit button, so cart drawer,
 * notifications and quantity rules all keep working untouched.
 */
class StickyAddToCart {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.form = document.getElementById(container.dataset.productFormId);
    this.submitButton = this.form ? this.form.querySelector('[name="add"]') : null;

    // Some products render a promo message instead of a buy button. Nothing to stick to.
    if (!this.submitButton) {
      this.container.remove();
      return;
    }

    this.button = container.querySelector('[data-sticky-add]');
    this.buttonText = this.button.querySelector('span');
    this.select = container.querySelector('[data-sticky-variant-select]');
    this.priceContainer = container.querySelector('[data-sticky-price]');
    this.addLabel = this.button.dataset.addLabel || window.variantStrings.addToCart;

    this.bindEvents();
    this.observeBuyButtons();
  }

  bindEvents() {
    this.button.addEventListener('click', this.onAddToCart.bind(this));

    if (this.select) {
      this.select.addEventListener('change', this.onVariantSelect.bind(this));
    }

    if (typeof subscribe !== 'function') return;

    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.setLoading(false));
    this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, () => this.setLoading(false));
  }

  disconnect() {
    if (this.observer) this.observer.disconnect();
    if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    if (this.cartErrorUnsubscriber) this.cartErrorUnsubscriber();
  }

  /*
   * The bar ships visible and gets out of the way whenever the real add to cart
   * button is on screen. The markup starts with .sticky-atc--init so that the
   * first correction, which lands a frame after load, snaps instead of sliding.
   */
  observeBuyButtons() {
    const target = document.getElementById(`ProductSubmitButton-${this.sectionId}`) || this.submitButton;

    if (!target) {
      this.container.classList.remove('sticky-atc--init');
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.container.classList.toggle('show', !entry.isIntersecting);
        });

        if (this.container.classList.contains('sticky-atc--init')) {
          requestAnimationFrame(() => this.container.classList.remove('sticky-atc--init'));
        }
      },
      { threshold: 0 }
    );

    this.observer.observe(target);
  }

  getVariantData() {
    if (!this.variantData) {
      const script = this.container.querySelector('[data-sticky-variants]');
      this.variantData = script ? JSON.parse(script.textContent) : [];
    }
    return this.variantData;
  }

  getVariantPicker() {
    return document.querySelector(
      `variant-selects[data-section="${this.sectionId}"], variant-radios[data-section="${this.sectionId}"]`
    );
  }

  /*
   * Push the selection into the real variant picker and let Dawn do the rest:
   * media, URL, price, form input and button state all update from its change
   * handler, and the variantChange event brings the new state back to us.
   */
  onVariantSelect() {
    const variant = this.getVariantData().find((item) => item.id.toString() === this.select.value);
    if (!variant) return;

    const picker = this.getVariantPicker();

    if (!picker) {
      this.updateFormVariant(variant.id);
      return;
    }

    if (picker.tagName === 'VARIANT-RADIOS') {
      Array.from(picker.querySelectorAll('fieldset')).forEach((fieldset, index) => {
        const input = Array.from(fieldset.querySelectorAll('input')).find(
          (option) => option.value === variant.options[index]
        );
        if (input) input.checked = true;
      });
    } else {
      Array.from(picker.querySelectorAll('select')).forEach((select, index) => {
        select.value = variant.options[index];
      });
    }

    picker.dispatchEvent(new Event('change', { bubbles: true }));
  }

  updateFormVariant(variantId) {
    const input = this.form.querySelector('input[name="id"]');
    if (!input) return;

    input.value = variantId;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onVariantChange(event) {
    const { variant, html, sectionId } = event.data;

    if (!variant) {
      this.toggleButton(true, window.variantStrings.unavailable);
      return;
    }

    if (this.select && this.select.value !== variant.id.toString()) {
      this.select.value = variant.id.toString();
    }

    const priceSource = html.getElementById(`StickyPriceWrapper-${sectionId}`);
    if (priceSource && this.priceContainer) {
      this.priceContainer.innerHTML = priceSource.innerHTML;
    }

    const submitSource = html.getElementById(`ProductSubmitButton-${sectionId}`);
    const disabled = submitSource ? submitSource.hasAttribute('disabled') : !variant.available;
    this.toggleButton(disabled, window.variantStrings.soldOut);
  }

  toggleButton(disabled, disabledText) {
    if (disabled) {
      this.button.setAttribute('disabled', 'disabled');
      if (this.buttonText && disabledText) this.buttonText.textContent = disabledText;
    } else {
      this.button.removeAttribute('disabled');
      if (this.buttonText) this.buttonText.textContent = this.addLabel;
    }
  }

  onAddToCart(event) {
    event.preventDefault();
    if (this.button.hasAttribute('disabled') || this.submitButton.hasAttribute('disabled')) return;

    this.setLoading(true);
    this.submitButton.click();

    // The cart events below clear the spinner. This is the safety net for the
    // cases that never publish one, such as a hard navigation to /cart.
    clearTimeout(this.loadingTimeout);
    this.loadingTimeout = setTimeout(() => this.setLoading(false), 10000);
  }

  setLoading(loading) {
    if (!loading) clearTimeout(this.loadingTimeout);

    this.button.classList.toggle('loading', loading);
    this.button.setAttribute('aria-busy', loading);

    const spinner = this.button.querySelector('.loading-overlay__spinner');
    if (spinner) spinner.classList.toggle('hidden', !loading);
  }
}

(function initStickyAddToCart() {
  function init() {
    document.querySelectorAll('[data-sticky-atc]').forEach((container) => {
      if (container.stickyAddToCart) return;
      container.stickyAddToCart = new StickyAddToCart(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-sticky-atc]').forEach((container) => {
      if (container.stickyAddToCart) container.stickyAddToCart.disconnect();
    });
  });

  // Keep the bar on screen while it is being edited in the theme editor.
  document.addEventListener('shopify:block:select', (event) => {
    if (event.target.matches('[data-sticky-atc]')) event.target.classList.add('show');
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    if (event.target.matches('[data-sticky-atc]')) event.target.classList.remove('show');
  });
})();
